import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingContactButton from "../components/FloatingContactButton";

const NotFound: React.FC = () => {
  const location = useLocation();
  const isSpecificPage = ["/about", "/services", "/gallery"].includes(
    location.pathname
  );

  // Force the navbar to have background on specific pages
  useEffect(() => {
    if (isSpecificPage) {
      const navbar = document.querySelector("nav");
      if (navbar) {
        navbar.classList.add(
          "bg-primary-900/95",
          "backdrop-blur-md",
          "shadow-lg"
        );
        navbar.classList.remove("bg-transparent");
      }
    }

    return () => {
      const navbar = document.querySelector("nav");
      if (navbar) {
        navbar.classList.remove(
          "bg-primary-900/95",
          "backdrop-blur-md",
          "shadow-lg"
        );
      }
    };
  }, [isSpecificPage]);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-neutral-50 to-neutral-100">
      <Navbar />

      {isSpecificPage && (
        <div className="absolute inset-0 z-[-1]">
          <div className="h-64 bg-primary-900 w-full"></div>
        </div>
      )}

      <div
        className={`flex-grow flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16 p-6 md:p-12 ${
          isSpecificPage ? "pt-24 md:pt-32" : ""
        }`}
      >
        <div className="text-center md:text-left">
          <h1 className="text-8xl md:text-9xl font-extrabold tracking-tighter">
            <span className="text-primary-500">4</span>
            <span className="text-primary-600">0</span>
            <span className="text-primary-700">4</span>
          </h1>

          <h2 className="mt-6 text-3xl md:text-4xl font-bold text-neutral-800">
            {isSpecificPage ? "Coming Soon!" : "Oops! Path Not Found"}
          </h2>

          <p className="mt-4 mb-8 text-lg text-neutral-600 max-w-md">
            {isSpecificPage
              ? "This page is currently under construction. We appreciate your patience while we work on it!"
              : "It seems like this page is currently unavailable or under construction. We're working on it!"}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
            <Link
              to="/"
              className="bg-primary-600 hover:bg-primary-700 text-white font-medium px-8 py-3 rounded-lg shadow-lg transition-all duration-300 hover:translate-y-[-2px] text-center"
            >
              Return Home
            </Link>
            <Link
              to="/contact"
              className="bg-white hover:bg-neutral-100 text-primary-600 border-2 border-primary-600 font-medium px-8 py-3 rounded-lg transition-all duration-300 hover:translate-y-[-2px] text-center"
            >
              Contact Support
            </Link>
          </div>
        </div>

        <div className="relative w-72 h-72 md:w-96 md:h-96">
          <div className="absolute top-0 left-0 w-full h-full animate-pulse">
            <svg
              viewBox="0 0 200 200"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full h-full text-primary-400 opacity-20"
            >
              <path
                fill="currentColor"
                d="M45.4,-77.1C59.4,-69.9,71.9,-59,78.9,-45.1C85.9,-31.1,87.3,-15.6,87.7,0.2C88,16,87.3,32,79.7,43.9C72.1,55.8,57.6,63.5,43.1,69.5C28.6,75.5,14.3,79.8,-1.3,81.9C-17,84,-34,83.8,-45.3,75.9C-56.7,67.9,-62.3,52.1,-67.9,37.2C-73.5,22.3,-79.2,8.4,-80.3,-6.2C-81.4,-20.8,-78,-36.1,-69.4,-47.7C-60.9,-59.3,-47.3,-67.2,-33.6,-74.3C-19.9,-81.4,-10,-87.7,2.4,-91.8C14.7,-95.9,31.4,-84.4,45.4,-77.1Z"
                transform="translate(100 100)"
              />
            </svg>
          </div>

          <div className="relative w-full h-full flex items-center justify-center">
            <div className="w-40 h-40 md:w-52 md:h-52 bg-white rounded-full shadow-xl flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-24 w-24 md:h-32 md:w-32 text-primary-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isSpecificPage ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                )}
              </svg>
            </div>
          </div>

          <div className="absolute -bottom-4 -right-4 w-16 h-16 bg-primary-200 rounded-full animate-bounce"></div>
          <div className="absolute -top-2 -left-2 w-10 h-10 bg-primary-300 rounded-full animate-ping opacity-75"></div>
        </div>
      </div>

      <Footer />
      <FloatingContactButton />
    </div>
  );
};

export default NotFound;
